<template>
	<div class="multi-currency" tabindex="0" @focus="onElementFocused">
		<div v-if="settings.targetValuePropertyName == 'debit' || settings.targetValuePropertyName == 'credit'">
		</div>
		<nk-label v-if="!Array.isArray(localModelValue)" :options="getDebitCreditInputOptions()" ref="dc" :tabindex="0" v-model="localModelValue" class="text-right"></nk-label>
		<div v-for="(item, itemIndex) of localModelValue" :key="item.id" class="mc-row">
			<nk-label v-if="settings.targetValuePropertyName == 'debit' || settings.targetValuePropertyName == 'credit' && itemIndex == 0" :options="getDebitCreditInputOptions()" ref="dc" :tabindex="itemIndex" v-model="item[settings.targetValuePropertyName]" class="text-right"></nk-label>
			<nk-label v-if="settings.targetValuePropertyName == 'currency' && item.currency" ref="dc" :options="{ ltr: false }" :tabindex="itemIndex" v-model="item.currency.name"></nk-label>
			<nk-label v-if="settings.targetValuePropertyName == 'currencyRate'" :options="getDebitCreditInputOptions()" ref="currencyRate" :tabindex="itemIndex" v-model="item[settings.targetValuePropertyName]"></nk-label>
			<nk-label v-if="settings.targetValuePropertyName == 'debitCurrency' || settings.targetValuePropertyName == 'creditCurrency'" :options="getInputOptions(item)" ref="dcCurrency" :tabindex="itemIndex" v-model="item[settings.targetValuePropertyName]"></nk-label>
		</div>
	</div>
</template>

<script>
	import _ from 'lodash'
	import { mapGetters } from 'vuex'

	export default {
		props: {
			modelValue: {},
			options: { type: Object },
		},
		emits: ['update:modelValue'],
		created() {
			this.localModelValue = this.modelValue
		},
		mounted() {
			this.prepareCurrency()
		},
		data() {
			return {
				localModelValue: null,
				finalizedValue: null,
				settings: {
					isNameVisible: true,
					isSignVisible: true,
					showOnlyDefaultCurrency: false,
				},
				currencyOptions: {
					type: 'price',
					ltr: true,
					decimals: 0
				},
				activeRowIndex: 0,
				componentOptions: {
					currency: {
						searchable: true,
						required: true,
						remoteSearch: false,
						display: function (item) { return item.name },
						searchableFields: ['name']
					}
				}
			}
		},
		computed: {
			...mapGetters({
				getAllCurrencies: 'currencies/companyCurrencies'
			}),
			rows() {
				return this.getAllCurrencies.map(currency => {
					return _.extend({}, currency, { value: this.rowValue(currency.id) })
				})
			},
		},
		watch: {
			options: {
				immediate: true,
				deep: true,
				handler(val) {
					this.settings = _.merge({}, this.settings, val)
				}
			},
			modelValue: {
				deep: true,
				handler(val) {
					this.localModelValue = val
					this.prepareCurrency()
				}
			}
		},
		methods: {
			prepareCurrency(value = this.localModelValue) {
				for (let index = 0; index < value.length; index++) {
					if (value[index].currency?.id) {
						value[index].currency = this.getAllCurrencies.find((x) => x.id === value[index].currency.id)
					}
				}
			},
			focusRow(index) {
				let inputs = this.$refs[index]
				if (inputs && inputs.length) {
					inputs[0].$el.focus()
					this.activeRowIndex = index
				}
			},
			rowValue(currencyId) {
				let item = _.head(this.localModelValue.filter(x => x.id === currencyId))
				return (item || {}).value || 0
			},
			checkActiveRowIndex() {
				let index = this.rows.findIndex((r, index) => this.$refs[`mcrow-${index}`] === document.activeElement)

				if (index > -1) {
					this.activeRowIndex = index
				}
			},
			getInputOptions(item) {
				let opt = null
				if (item.currency && item.currency.decimals > 0) {
					opt = { decimals: item.currency.decimals }
				}
				else if (!item.currency) {
					opt = { decimals: 2 } // If the currency is not sent from the server, we set the decimals to 2, so we can show the decimal numbers as well
				}
				return _.extend(this.currencyOptions, opt)
			},
			getDebitCreditInputOptions() {
				let opt = null
				var defaultCurrency = this.getAllCurrencies.find(x => x.IsDefault && x.IsEnabled)
				if (defaultCurrency && defaultCurrency.decimals > 0) {
					opt = { decimals: defaultCurrency.decimals }
				}
				return _.extend(this.currencyOptions, opt)
			},
			onCurrencyRateFocus(item) {
				if (item.calcDebitSource === '') {
					if (item.debit != 0)
						item.calcDebitSource = 'debit'
					else
						item.calcDebitSource = 'debitCurrency'
				}

				if (item.calcCreditSource === '') {
					if (item.credit != 0)
						item.calcCreditSource = 'credit'
					else
						item.calcCreditSource = 'creditCurrency'
				}
			},
			onDebitCreditValueChanged(value, item) {
				for (var row of this.value) {
					row[this.settings.targetValuePropertyName] = value
				}
				if (item.currencyRate)
					item[this.settings.targetValuePropertyName + 'Currency'] = value / item.currencyRate

				item.calcCreditSource = 'credit'
				item.calcDebitSource = 'debit'

				this.$emit('update:modelValue', this.localModelValue)
			},
			onDCCurrencyValueChanged(value, item) {
				item[this.settings.targetValuePropertyName.replace('Currency', '')] = value * item.currencyRate

				item.calcCreditSource = 'creditCurrency'
				item.calcDebitSource = 'debitCurrency'

				this.$emit('update:modelValue', this.localModelValue)
			},
			onCurrencyRateChanged(val, item) {
				if (item.calcCreditSource == 'creditCurrency')
					item.credit = item.creditCurrency * val
				else if (val != 0)
					item.creditCurrency = item.credit / val

				if (item.calcDebitSource == 'debitCurrency')
					item.debit = item.debitCurrency * val
				else if (val != 0)
					item.debitCurrency = item.debit / val
			},
			onElementFocused() {
				this.focusRow(0)
			},
			subscribeKeys() {
				this.$keyHandler.Subscribe({
					name: 'multi-currency-forward',
					keyCode: [40, 13, 9],
					ctrlKey: false,
					altKey: false,
					when: 'before',
					el: this.$el,
					context: this,
					handler: (sender, e) => {
						e.preventDefault()

						this.checkActiveRowIndex()

						var handled = true

						if (this.activeRowIndex < this.localModelValue.length - 1) {
							let index = this.activeRowIndex + 1
							this.focusRow(index)
						} else {
							handled = false
						}

						return handled
					}
				}) // forward

				this.$keyHandler.Subscribe({
					name: 'multi-currency-backward',
					keyCode: [38],
					when: 'before',
					el: this.$el,
					context: this,
					handler: function (sender, e) {
						e.preventDefault()

						this.checkActiveRowIndex()

						var handled = true

						if (this.activeRowIndex > 0) {
							let index = this.activeRowIndex - 1
							this.focusRow(index)
						} else {
							handled = false
						}

						return handled
					}
				}) // backward

				this.$keyHandler.Subscribe({
					name: 'multi-currency-backward-shift',
					keyCode: [9],
					shiftKey: true,
					when: 'before',
					el: this.$el,
					context: this,
					handler: function (sender, e) {
						e.preventDefault()

						this.checkActiveRowIndex()

						var handled = true

						if (this.activeRowIndex > 0) {
							let index = this.activeRowIndex - 1
							this.focusRow(index)
						} else {
							handled = false
						}

						return handled
					}
				}) // backward with shift
			}
		}
	}
</script>

<style lang="less" src="~/styles/multi-currency.less"></style>
